import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Flex,
  Text,
} from '@kognitos/lattice';
import { resolveCallArgs } from '@/modules/automation/utils/resolve-call-args';
import { RunTableOutputPreview } from '@/modules/outputs/components/RunTableOutputPreview';
import { ValueList } from '@/shared/components/ValueList';
import {
  formatDisplayName,
  formatProcedureLabel,
  getInitials,
} from '@/shared/utils';
import type { TResolvedValue } from '@/modules/outputs/types';
import type { IResolvedExternalCall } from '../../utils/external-call-visibility';
import { useBookIdentity } from '../../hooks/useBookIdentity';
import { invocationKey } from '@/modules/automation-run/hooks/useRunJournalEvents';
import type { TRunLogSpineRow } from './RunLogSpine';

const MAX_INPUT_ROWS = 4;

/**
 * An integration's logo in a spine circle, falling back to initials.
 *
 * The 20px logo isn't clipped to the 24px circle: a circle crops a square
 * logo's corners.
 *
 * @param props - The book whose logo to show
 * @returns {JSX.Element} The logo
 */
export function BookSpineIcon({ bookName }: { bookName?: string }) {
  const { t } = useTranslation('automation');
  const { icon, displayName } = useBookIdentity(bookName);
  const label = displayName || bookName || t('automation.runLog.unknownBook');

  return (
    <Avatar className="size-6 items-center justify-center overflow-visible rounded-full">
      {icon && (
        <AvatarImage src={icon} alt={label} className="size-5 object-contain" />
      )}
      <AvatarFallback className="rounded-full bg-muted text-xs font-medium text-muted-foreground">
        {getInitials(label)}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * The integration's display name, for the logo's tooltip.
 *
 * @param props - The book to name
 * @returns {JSX.Element} The name
 */
function BookName({ bookName }: { bookName?: string }) {
  const { t } = useTranslation('automation');
  const { displayName } = useBookIdentity(bookName);
  return <>{displayName || bookName || t('automation.runLog.unknownBook')}</>;
}

/**
 * One call: the action it performed, what it used, then its output in a
 * preview box.
 *
 * @param props - The resolved call and how to open its output
 * @returns {JSX.Element} The call content
 */
function ExternalCallContent({
  call,
  output,
  onOpenOutput,
}: IResolvedExternalCall & {
  onOpenOutput?: (name: string, value: TResolvedValue) => void;
}) {
  const { t } = useTranslation('automation');
  const rawProcedure = call.bookFunction?.functionName ?? call.functionName;
  const procedureLabel = formatProcedureLabel(rawProcedure, t);
  const inputs = resolveCallArgs(undefined, undefined, call.inputs);

  return (
    <>
      <Text level="small" title={rawProcedure} className="min-h-6 truncate">
        {procedureLabel}
      </Text>
      {/* Wider than the row's gap to match the space `min-h-6` leaves under the title. */}
      {(inputs.length > 0 || output != null) && (
        <Flex direction="column" align="stretch" gap={3}>
          {inputs.length > 0 && (
            <Flex direction="column" align="stretch" gap={1}>
              <Text level="xSmall" color="muted" weight="medium">
                {t('automation.runLog.call.uses')}
              </Text>
              <ValueList
                entries={inputs.map(({ name, value }) => [name, value])}
                ariaLabel={procedureLabel}
                maxRows={MAX_INPUT_ROWS}
              />
            </Flex>
          )}
          {output != null && (
            <Flex direction="column" align="stretch" gap={1}>
              <Text level="xSmall" color="muted" weight="medium">
                {t('automation.runLog.call.returns')}
              </Text>
              <RunTableOutputPreview
                name={procedureLabel}
                value={output}
                onOpen={
                  onOpenOutput
                    ? () => onOpenOutput(procedureLabel, output)
                    : undefined
                }
                variant="bare"
                renderInline
              />
            </Flex>
          )}
        </Flex>
      )}
    </>
  );
}

/**
 * Spine rows for a step's external calls: one per call, in execution order,
 * each with the integration's logo as its icon and its inputs and output under
 * it.
 *
 * @param calls - The step's calls with their resolved outputs
 * @param t - Translator for the row label
 * @param onOpenOutput - Opens a call's output in the host's preview layover
 * @returns Rows to place between the inputs and the output
 */
export function buildExternalCallRows(
  calls: IResolvedExternalCall[],
  t: TFunction<'automation'>,
  onOpenOutput?: (name: string, value: TResolvedValue) => void,
): TRunLogSpineRow[] {
  return calls.map(entry => {
    const bookName = entry.call.bookFunction?.bookName;
    return {
      key: `call-${invocationKey(entry.call)}`,
      icon: 'Plug',
      iconNode: <BookSpineIcon bookName={bookName} />,
      label: bookName
        ? formatDisplayName(bookName)
        : t('automation.runLog.externalAction'),
      tooltip: <BookName bookName={bookName} />,
      content: (
        <ExternalCallContent
          call={entry.call}
          output={entry.output}
          onOpenOutput={onOpenOutput}
        />
      ),
    };
  });
}
